import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  getFichiersProjet, uploadFichierProjet, openFichierProjet, deleteFichierProjet, getEtapes, getJalons,
} from "../services/api";
import ModaleV2 from "./ModaleV2";
import OfficeViewer from "./OfficeViewer";
import { formatDate, formatTaille } from "../services/projet";

// ===================================================================
// The Crowd v2 — Fiche projet : documents
//
// Un document se rattache au projet, ou a une etape, ou a un jalon.
// Depot : toute personne ayant un role sur le projet (peut_deposer).
// Suppression : l'auteur du depot, ou le chef et les co-chefs.
// Ouverture : OnlyOffice pour les documents bureautiques (composant
// OfficeViewer existant), nouvel onglet pour le reste.
//
// Props : projetId, collaborateurId, droits
// ===================================================================

function rattachementDe(f) {
  if (f.etape_id) return `Étape : ${f.etape_nom || f.etape_id}`;
  if (f.jalon_id) return `Jalon : ${f.jalon_intitule || f.jalon_id}`;
  return "Projet";
}

function ProjetDocumentsV2({ projetId, collaborateurId, droits }) {
  const [fichiers, setFichiers] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [message, setMessage] = useState(null);
  const [etapes, setEtapes] = useState([]);
  const [jalons, setJalons] = useState([]);
  const [fichier, setFichier] = useState(null);
  const [cible, setCible] = useState("projet"); // "projet" | "e:<id>" | "j:<id>"
  const [depot, setDepot] = useState(false);
  const [aSupprimer, setASupprimer] = useState(null);
  const [suppressionEnCours, setSuppressionEnCours] = useState(false);
  const [viewer, setViewer] = useState(null); // { file: { name }, url }
  const champFichier = useRef(null);

  const peutDeposer = Boolean(droits?.peut_deposer);

  const charger = useCallback(() => {
    setErreur(null);
    return getFichiersProjet(projetId, collaborateurId)
      .then(setFichiers)
      .catch((err) => setErreur(`Documents non chargés : ${err.message}`));
  }, [projetId, collaborateurId]);

  useEffect(() => {
    charger();
  }, [charger]);

  // Cibles de rattachement : seulement pour qui peut deposer
  useEffect(() => {
    if (!peutDeposer) return;
    getEtapes(projetId, collaborateurId).then((d) => setEtapes(d.etapes || [])).catch(() => setEtapes([]));
    getJalons(projetId, collaborateurId).then(setJalons).catch(() => setJalons([]));
  }, [projetId, collaborateurId, peutDeposer]);

  const deposer = async () => {
    if (!fichier) return;
    setDepot(true);
    setMessage(null);
    try {
      const rattachement = {};
      if (cible.startsWith("e:")) rattachement.etapeId = Number(cible.slice(2));
      if (cible.startsWith("j:")) rattachement.jalonId = Number(cible.slice(2));
      await uploadFichierProjet(projetId, fichier, collaborateurId, rattachement);
      setFichier(null);
      setCible("projet");
      if (champFichier.current) champFichier.current.value = "";
      setMessage({ type: "succes", texte: "Document déposé." });
      await charger();
    } catch (err) {
      setMessage({ type: "erreur", texte: `Dépôt impossible : ${err.message}` });
    } finally {
      setDepot(false);
    }
  };

  const ouvrir = async (f) => {
    setMessage(null);
    try {
      const r = await openFichierProjet(projetId, f.id, collaborateurId);
      if (r.viewer === "onlyoffice") {
        setViewer({ file: { name: r.nom }, url: r.sas_url });
      } else {
        window.open(r.sas_url, "_blank", "noopener");
      }
    } catch (err) {
      setMessage({ type: "erreur", texte: `Ouverture impossible : ${err.message}` });
    }
  };

  const supprimer = async () => {
    setSuppressionEnCours(true);
    try {
      await deleteFichierProjet(projetId, aSupprimer.id, collaborateurId);
      setASupprimer(null);
      await charger();
    } catch (err) {
      setMessage({ type: "erreur", texte: `Suppression impossible : ${err.message}` });
      setASupprimer(null);
    } finally {
      setSuppressionEnCours(false);
    }
  };

  const peutSupprimer = (f) => Boolean(droits?.peut_gerer) || (f.depose_par && f.depose_par === collaborateurId);

  return (
    <section className="v2-panneau v2-projet-bloc">
      <div className="v2-bloc-entete">
        <h2 className="v2-bloc-titre">Documents</h2>
      </div>

      {peutDeposer && (
        <div className="v2-projet-depot">
          <div className="v2-champ">
            <label className="v2-label" htmlFor="doc-fichier">Fichier</label>
            <input id="doc-fichier" ref={champFichier} type="file" className="v2-champ-fichier"
              onChange={(e) => setFichier(e.target.files?.[0] || null)} />
          </div>
          <div className="v2-champ">
            <label className="v2-label" htmlFor="doc-cible">Rattacher à</label>
            <select id="doc-cible" value={cible} onChange={(e) => setCible(e.target.value)}>
              <option value="projet">Projet</option>
              {etapes.length > 0 && (
                <optgroup label="Étapes">
                  {etapes.map((e) => <option key={`e${e.id}`} value={`e:${e.id}`}>{e.nom}</option>)}
                </optgroup>
              )}
              {jalons.length > 0 && (
                <optgroup label="Jalons">
                  {jalons.map((j) => <option key={`j${j.id}`} value={`j:${j.id}`}>{j.intitule}</option>)}
                </optgroup>
              )}
            </select>
          </div>
          <button type="button" className="v2-btn v2-btn-principal" onClick={deposer} disabled={!fichier || depot}>
            {depot ? "Dépôt…" : "Déposer"}
          </button>
        </div>
      )}

      {message && (
        <div className={`v2-msg ${message.type === "erreur" ? "v2-msg-erreur" : "v2-msg-succes"} v2-marge-bas`} role="status">
          {message.texte}
        </div>
      )}
      {erreur && <div className="v2-msg v2-msg-erreur v2-marge-bas">{erreur}</div>}
      {!fichiers && !erreur && <div className="v2-chargement">Chargement…</div>}

      {fichiers && (
        <div className="v2-tableau-conteneur">
          <table className="v2-tableau">
            <thead>
              <tr>
                <th scope="col">Document</th>
                <th scope="col">Rattaché à</th>
                <th scope="col">Déposé par</th>
                <th scope="col">Date</th>
                <th scope="col" className="v2-num">Taille</th>
                <th scope="col" className="v2-projet-col-actions"><span className="v2-invisible">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {fichiers.length === 0 && (
                <tr><td colSpan={6} className="v2-vide">Aucun document.</td></tr>
              )}
              {fichiers.map((f) => (
                <tr key={f.id}>
                  <td>
                    <button type="button" className="v2-lien-ligne" onClick={() => ouvrir(f)}>{f.nom_original}</button>
                  </td>
                  <td className="v2-cellule-discrete">{rattachementDe(f)}</td>
                  <td className="v2-cellule-discrete">{f.depose_par_nom || "—"}</td>
                  <td className="v2-cellule-discrete">{formatDate(f.uploaded_at)}</td>
                  <td className="v2-num v2-cellule-discrete">{formatTaille(f.taille_octets)}</td>
                  <td>
                    {peutSupprimer(f) && (
                      <button type="button" className="v2-btn v2-btn-secondaire v2-btn-compact v2-btn-danger-texte"
                        onClick={() => setASupprimer(f)}>
                        Supprimer
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {aSupprimer && (
        <ModaleV2
          titre="Supprimer le document"
          onClose={() => setASupprimer(null)}
          bloquee={suppressionEnCours}
          actions={
            <>
              <button type="button" className="v2-btn v2-btn-secondaire" onClick={() => setASupprimer(null)} disabled={suppressionEnCours}>
                Annuler
              </button>
              <button type="button" className="v2-btn v2-btn-secondaire v2-btn-danger-texte" onClick={supprimer} disabled={suppressionEnCours}>
                {suppressionEnCours ? "Suppression…" : "Supprimer"}
              </button>
            </>
          }
        >
          <p>« {aSupprimer.nom_original} » sera supprimé définitivement.</p>
        </ModaleV2>
      )}

      {viewer && <OfficeViewer file={viewer.file} url={viewer.url} onClose={() => setViewer(null)} />}
    </section>
  );
}

export default ProjetDocumentsV2;
